import React from "react";
import { ArrowLeft, Lock, Send } from "lucide-react";
import { Localizer } from "@/main/react/Types";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import TextAreaWithCharCounter from "@/main/react/component/TextAreaWithCharCounter";
import { InfoBanner } from "@/main/react/component/InfoBanner";
import { REQUEST_VALIDATION_LIMITS } from "@/main/validation/request";

type Props = {
    localizer: Localizer;
    backLabel: string;
    targetLabel: string;
    noteInputId: string;
    note: string;
    submitting: boolean;
    onBack: () => void;
    onCancel: () => void;
    onSubmit: () => void;
    onNoteChange: (nextValue: string) => void;
    children: React.ReactNode;
};

export function DiscussionAddToGroupRequestForm({
    localizer,
    backLabel,
    targetLabel,
    noteInputId,
    note,
    submitting,
    onBack,
    onCancel,
    onSubmit,
    onNoteChange,
    children,
}: Props) {
    return (
        <div className="flex w-full flex-col gap-4">
            <Button
                type="button"
                variant="ghost"
                className="w-fit px-0 text-sm text-neutral-text"
                onClick={onBack}
                disabled={submitting}
            >
                <ArrowLeft className="size-4" />
                {backLabel}
            </Button>

            <InfoBanner
                icon={Lock}
                title={localizer("discussion.request.modal.form_title")}
                description={localizer("discussion.request.modal.form_description")}
            />

            <div className="w-full rounded-xl border border-border bg-background px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted">
                    {targetLabel}
                </div>

                <div className="mt-3">{children}</div>
            </div>

            <div className="w-full space-y-2">
                <TextAreaWithCharCounter
                    id={noteInputId}
                    name={noteInputId}
                    value={note}
                    onChange={(e) => onNoteChange(e.target.value)}
                    floatingLabel={localizer("discussion.request.modal.note_label")}
                    wrapperClassName="w-full"
                    rows={5}
                    maxLength={REQUEST_VALIDATION_LIMITS.longText.maxLength}
                    disabled={submitting}
                />
                <p className="text-xs text-neutral-text">
                    {localizer("discussion.request.modal.note_help")}
                </p>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    {localizer("discussion.request.modal.cancel")}
                </Button>
                <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={submitting}
                >
                    {submitting ? (
                        <Spinner className="size-4 text-current" />
                    ) : (
                        <Send className="size-4" aria-hidden="true" />
                    )}
                    {localizer("discussion.request.modal.submit")}
                </Button>
            </div>
        </div>
    );
}
